import "./css/App.css";
import React, { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import Home from "./pages/public/Home";
import Register from "./pages/public/register/RegisterLayout";
import SpotRegisterAthlete from "./pages/public/register/SpotRegisterAthlete";
import CombineBundleStep from "./pages/public/register/CombineBundleStep";
import Leaderboard from "./components/leaderboard/leaderboardtable";
import ContactUs from "./components/Contact";
import SignIn from "./pages/public/SignIn";
import ProfileShared from "./components/profile-shared";
// Athlete pages
import AthleteLayout from "./pages/athlete/AthleteLayout";
import AthleteDashboard from "./pages/athlete/dashboard/AthleteDashboard";
import AthleteEvents from "./pages/athlete/events/AthleteEvents";
import AthleteCombine from "./pages/athlete/combine-results/AthleteCombine";
import AthleteAccount from "./pages/athlete/account/AthleteAccount";
// University/Coach Pages
import UniversityLayout from "./pages/coach/CoachLayout";
import UniversityDashboard from "./pages/coach/dashboard/CoachDashboard";
import UniversityAthletes from "./pages/coach/athletes/CoachAthletes";
import UniversityCombine from "./pages/coach/combine-results/CoachCombine";
import UniversityAccount from "./pages/coach/account/CoachAccount";
// High School Pages
import HighSchoolLayout from "./pages/highschool/layout/HighSchoolLayout";
import HighSchoolDashboardPage from "./pages/highschool/dashboard/HighSchoolDashboardPage";
import HighSchoolLeaderboardPage from "./pages/highschool/leaderboard/HighSchoolLeaderboardPage";
import HighSchoolAccountPage from "./pages/highschool/account/HighSchoolAccountPage";
// Admin Pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/dashboard/AdminDashboard";
import AdminAccount from "./pages/admin/account/AdminAccount";
import AdminAthlete from "./pages/admin/athletes/AdminAthlete";
import AdminCoaches from "./pages/admin/coaches/AdminCoaches";
import AdminCombine from "./pages/admin/combines/AdminCombine";
import AdminCalendar from "./pages/admin/calendar/AdminCalendar";
import AdminSettings from "./pages/admin/settings/AdminSettings";
import AdminCandidateAthletes from "./pages/admin/candidate-athletes/AdminCandidateAthletes";
import AdminDuplicateUsers from "./pages/admin/duplicate-users/AdminDuplicateUsers";
import CoachRegister from "./pages/public/register/CoachRegister";
import AdminRegistering from "./pages/admin/register/AdminRegistering";
import ProtectedRoute from "./components/ProtectedRoute";
import Unauthorized from "./pages/public/Unauthorized";
import Accolade from "./pages/athlete/dashboard/Accolade";
import OfferForm from "./pages/athlete/dashboard/Offer";
import ConfirmRegistration from "./components/ConfirmRegistration";
import PaymentOption from "./pages/public/register/Payment";
import ThankYou from "./components/RegistrationSuccess";
import ForgotPassword from "./pages/public/ForgotPassword";
import ResetPassword from "./pages/public/ResetPassword";
import Events from "./pages/public/events/Events";
import { RegisterAthlete } from "./pages/public/register/registerAthlete/RegisterAthlete";
import PaymentUpdate from "./components/PaymentUpdate";
import AthleteMembershipPlans from "./components/AthleteMembershipPlans";
import { useDispatch, useSelector } from "react-redux";
import { validateSubscriptionAction } from "./store/userActions";
import { useAxiosInstance } from "./services/Interceptor";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import useIsAuthenticated from "react-auth-kit/hooks/useIsAuthenticated";
import useSignOut from "react-auth-kit/hooks/useSignOut";
import AthleteFreeSubscription from "./pages/public/register/registerAthlete/athleteFreeSubscription";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AUTH_REFRESH_FAILED_EVENT } from "./Refresh";
import WaiverAgreement from "./pages/public/register/WaiverAgreement";
import NewCoachRegistration from "./pages/coach/NewCoachRegistration";
import AutoLogin from "./pages/public/AutoLogin";
import PublicLink from "./pages/public/PublicLink";
import { broadcastLogout, subscribeToLogout } from "./utils/authChannel";

function App() {
  const userRoles = ["admin", "athlete", "university", "highschool"];
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const auth = useAuthUser();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const hasSubscription = useSelector((state) => state.user.hasSubscription);
  const location = useLocation();

  // Handle 401s and refresh failures with smooth client-side navigation (no hard reload)
  useEffect(() => {
    const handleAuthFailure = () => {
      signOut();
      broadcastLogout();
      navigate("/sign-in", { replace: true });
    };
    window.addEventListener(AUTH_REFRESH_FAILED_EVENT, handleAuthFailure);
    return () =>
      window.removeEventListener(AUTH_REFRESH_FAILED_EVENT, handleAuthFailure);
  }, [signOut, navigate]);

  // Cross-tab logout: another tab signing out broadcasts here so this tab
  // ends its session and redirects immediately, without needing a manual refresh.
  useEffect(() => {
    const handleCrossTabLogout = () => {
      signOut();
      navigate("/sign-in", { replace: true });
    };
    return subscribeToLogout(handleCrossTabLogout);
  }, [signOut, navigate]);

  // When the tab becomes visible again (e.g. phone unlocked after a long sleep),
  // check if the session is gone and redirect immediately instead of showing a grey page.
  // Only redirect when the current page actually requires authentication — public routes
  // (home, sign-in, register, events, shared profiles, etc.) must not be affected.
  useEffect(() => {
    const PROTECTED_LAYOUT_PREFIXES = [
      "/athlete/",
      "/university/",
      "/highschool/",
      "/admin/",
    ];
    // athlete-profile sub-routes are public shared profiles inside protected layouts
    const PUBLIC_PATH_PATTERNS = [
      "/athlete-profile/",
      "/admin/register",
      "/highschool/register",
    ];

    const isProtectedPath = (pathname) =>
      PROTECTED_LAYOUT_PREFIXES.some((prefix) => pathname.startsWith(prefix)) &&
      !PUBLIC_PATH_PATTERNS.some((pattern) => pathname.includes(pattern));

    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        !isAuthenticated &&
        isProtectedPath(location.pathname)
      ) {
        signOut();
        navigate("/sign-in", { replace: true });
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [isAuthenticated, signOut, navigate, location.pathname]);

  useEffect(() => {
    if (!auth && location.pathname.includes("athlete-profile")) {
      const profileId = location.pathname.split("/")[3];
      window.location.href = `/sign-in?back-to-profile=true&profile=${profileId}`;
    }
  }, [auth, location.pathname]);

  useEffect(() => {
    const HighSchoolCoach = auth?.user?.role?.toLowerCase?.() === "highschool";

    if (auth && auth.user && !HighSchoolCoach) {
      dispatch(
        validateSubscriptionAction(apiInstance, auth.user.role, auth.user.id),
      );
    }
  }, [auth, dispatch, apiInstance]);

  return (
    <div className="App" id="myApp">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/auto-login" element={<AutoLogin />} />
        {/* Registration Routes */}
        <Route path="/register" element={<Register />}>
          <Route index path="" element={<Navigate to="athlete" />} />
          <Route path="athlete" element={<RegisterAthlete />} />
          <Route path="coach" element={<CoachRegister />} />
          <Route path="subscription" element={<PaymentOption />} />
          <Route path="registrationsuccess" element={<ThankYou />} />
          <Route
            path="athletSubscription"
            element={<AthleteFreeSubscription />}
          />
        </Route>

        <Route path="/highschool/register" element={<NewCoachRegistration />} />

        <Route
          path="/athlete-spotregister/:eventId"
          element={<SpotRegisterAthlete />}
        />
        <Route path="/waiver-agreement" element={<WaiverAgreement />} />
        <Route path="/athlete-combine-bundle" element={<CombineBundleStep />} />
        <Route
          path="athlete-membership"
          element={<AthleteMembershipPlans />}
        ></Route>
        <Route path="/admin/register" element={<AdminRegistering />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/Contact" element={<ContactUs />} />

        {hasSubscription && (
          <Route path="/leaderboard" element={<Leaderboard />} />
        )}

        <Route path="/events" element={<Events />} />
        <Route path="/events/:eventId" element={<Events />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/public-link/:publicToken" element={<PublicLink />} />
        {/* Athlete Routes */}
        <Route path="athlete/*" element={<AthleteLayout />}>
          <Route
            path={`athlete-profile/:profileId`}
            element={<ProfileShared />}
          />
          <Route
            path="dashboard/*"
            element={
              <ProtectedRoute allowedRole={userRoles[1]}>
                <AthleteDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="events/*"
            element={
              <ProtectedRoute allowedRole={userRoles[1]}>
                <AthleteEvents />
              </ProtectedRoute>
            }
          />
          <Route
            path="combine"
            element={
              <ProtectedRoute allowedRole={userRoles[1]}>
                <AthleteCombine />
              </ProtectedRoute>
            }
          />
          <Route
            path="account"
            element={
              <ProtectedRoute allowedRole={userRoles[1]}>
                <AthleteAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path="account/:userId"
            element={
              <ProtectedRoute allowedRole={userRoles[1]}>
                <AthleteAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path="accolade"
            element={
              <ProtectedRoute allowedRole={userRoles[1]}>
                <Accolade />
              </ProtectedRoute>
            }
          />
          <Route
            path="offer"
            element={
              <ProtectedRoute allowedRole={userRoles[1]}>
                <OfferForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="paymentupdate"
            element={
              <ProtectedRoute allowedRole={userRoles[1]}>
                <PaymentUpdate />
              </ProtectedRoute>
            }
          />
        </Route>
        {/* University/Coach Routes */}
        <Route path="university" element={<UniversityLayout />}>
          <Route
            path={`athlete-profile/:profileId`}
            element={<ProfileShared />}
          />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute allowedRole={userRoles[2]}>
                <UniversityDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="athletes"
            element={
              <ProtectedRoute allowedRole={userRoles[2]}>
                <UniversityAthletes />
              </ProtectedRoute>
            }
          />
          <Route
            path="combine"
            element={
              <ProtectedRoute allowedRole={userRoles[2]}>
                <UniversityCombine />
              </ProtectedRoute>
            }
          />
          <Route
            path="account"
            element={
              <ProtectedRoute allowedRole={userRoles[2]}>
                <UniversityAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path="account/:userId"
            element={
              <ProtectedRoute allowedRole={userRoles[2]}>
                <UniversityAccount />
              </ProtectedRoute>
            }
          />
        </Route>
        {/* High School Routes */}
        <Route path="highschool" element={<HighSchoolLayout />}>
          <Route
            path={`athlete-profile/:profileId`}
            element={<ProfileShared />}
          />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute allowedRole={[userRoles[3], "highschool"]}>
                <HighSchoolDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="leaderboard"
            element={
              <ProtectedRoute allowedRole={[userRoles[3], "highschool"]}>
                <HighSchoolLeaderboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="account"
            element={
              <ProtectedRoute allowedRole={[userRoles[3], "highschool"]}>
                <HighSchoolAccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="account/:userId"
            element={
              <ProtectedRoute allowedRole={[userRoles[3], "highschool"]}>
                <HighSchoolAccountPage />
              </ProtectedRoute>
            }
          />
        </Route>
        {/* Admin Routes */}
        <Route path="admin" element={<AdminLayout />}>
          <Route
            path={`athlete-profile/:profileId`}
            element={<ProfileShared />}
          />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute allowedRole={userRoles[0]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="account"
            element={
              <ProtectedRoute allowedRole={userRoles[0]}>
                <AdminAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path="athletes"
            element={
              <ProtectedRoute allowedRole={userRoles[0]}>
                <AdminAthlete />
              </ProtectedRoute>
            }
          />
          <Route
            path="coaches"
            element={
              <ProtectedRoute allowedRole={userRoles[0]}>
                <AdminCoaches />
              </ProtectedRoute>
            }
          />
          {[
            "combine",
            "combine/measurements-video/:measurementId/:athleteId/:eventId/:type",
          ].map((item, index) => (
            <Route
              key={index}
              path={item}
              element={
                <ProtectedRoute allowedRole={userRoles[0]}>
                  <AdminCombine />
                </ProtectedRoute>
              }
            />
          ))}
          <Route
            path="calendar"
            element={
              <ProtectedRoute allowedRole={userRoles[0]}>
                <AdminCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="settings"
            element={
              <ProtectedRoute allowedRole={userRoles[0]}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="candidate-athletes"
            element={
              <ProtectedRoute allowedRole={userRoles[0]}>
                <AdminCandidateAthletes />
              </ProtectedRoute>
            }
          />
          <Route
            path="duplicate-users"
            element={
              <ProtectedRoute allowedRole={userRoles[0]}>
                <AdminDuplicateUsers />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path="confirm-registration"
          element={<ConfirmRegistration />}
        ></Route>
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        pauseOnFocusLoss
        containerId="modal"
      />
    </div>
  );
}

export default App;
